import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Heart, Building2, MapPin, Phone,
  AlertCircle, Loader2, ChevronDown, Info, CheckCircle, Users, Camera
} from 'lucide-react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import { useModeration, MODERATION_CONTEXTS, MODERATION_ACTIONS } from '../hooks/useModeration';
import { resolveBackendImage } from '../utils/brandLogo';

// Enums/Options
const ENTITY_TYPES = [
  { value: 'ong', label: 'ONG' },
  { value: 'osc', label: 'Organização da Sociedade Civil (OSC)' },
  { value: 'oscip', label: 'OSCIP' },
  { value: 'associacao', label: 'Associação' },
  { value: 'fundacao', label: 'Fundação' },
  { value: 'instituto', label: 'Instituto' },
  { value: 'projeto_independente', label: 'Projeto Independente' },
  { value: 'projeto_pessoa_fisica', label: 'Projeto de Pessoa Física' },
  { value: 'outro', label: 'Outro' }
];

const RESPONSIBLE_NATURE = [
  { value: 'pessoa_juridica', label: 'Pessoa Jurídica (com CNPJ)' },
  { value: 'pessoa_fisica', label: 'Pessoa Física' }
];

const ZONES = [
  { value: 'urbana', label: 'Zona Urbana' },
  { value: 'rural', label: 'Zona Rural' }
];

const AREAS_OF_ACTION = [
  { value: 'assistencia_social', label: 'Assistência Social', emoji: '🤝' },
  { value: 'cultura', label: 'Cultura', emoji: '🎭' },
  { value: 'esporte', label: 'Esporte', emoji: '⚽' },
  { value: 'educacao', label: 'Educação', emoji: '📚' },
  { value: 'saude', label: 'Saúde', emoji: '🏥' },
  { value: 'meio_ambiente', label: 'Meio Ambiente', emoji: '🌱' },
  { value: 'causa_animal', label: 'Causa Animal', emoji: '🐾' },
  { value: 'direitos_humanos', label: 'Direitos Humanos', emoji: '✊' },
  { value: 'empreendedorismo_social', label: 'Empreendedorismo Social', emoji: '💡' },
  { value: 'outros', label: 'Outros', emoji: '📌' }
];

const SUBMITTER_ROLES = [
  { value: 'responsavel_legal', label: 'Responsável Legal / Presidente' },
  { value: 'membro_diretoria', label: 'Membro da Diretoria' },
  { value: 'coordenador_projeto', label: 'Coordenador do Projeto' },
  { value: 'voluntario_autorizado', label: 'Voluntário Autorizado' },
  { value: 'representante', label: 'Representante' },
  { value: 'outro', label: 'Outro' }
];

const RELATIONSHIPS = [
  { value: 'fundador', label: 'Fundador(a)' },
  { value: 'funcionario', label: 'Funcionário(a)' },
  { value: 'voluntario', label: 'Voluntário(a)' },
  { value: 'parceiro', label: 'Parceiro(a)' },
  { value: 'outro', label: 'Outro' }
];

const TEMPO_ATUACAO_OPTIONS = [
  { value: 'menos_1_ano', label: 'Menos de 1 ano' },
  { value: '1_3_anos', label: 'De 1 a 3 anos' },
  { value: '3_5_anos', label: 'De 3 a 5 anos' },
  { value: 'mais_5_anos', label: 'Mais de 5 anos' }
];

// Components
const ProgressBar = ({ currentStep, totalSteps }) => {
  const progress = (currentStep / totalSteps) * 100;
  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="bg-gradient-to-r from-purple-600 to-pink-500 h-2 rounded-full transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

const FormField = ({ label, required, children, error, hint }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const SelectField = ({ value, onChange, options, placeholder, ...props }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-xl border border-gray-200 appearance-none bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
      {...props}
    >
      <option value="">{placeholder || 'Selecione...'}</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
  </div>
);

const CheckboxGroup = ({ options, selected, onChange, columns = 2 }) => (
  <div className={`grid gap-2 ${columns === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
    {options.map(opt => (
      <button
        key={opt.value}
        type="button"
        onClick={() => {
          const newSelected = selected.includes(opt.value)
            ? selected.filter(v => v !== opt.value)
            : [...selected, opt.value];
          onChange(newSelected);
        }}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-sm transition-all min-w-0 ${
          selected.includes(opt.value)
            ? 'border-purple-500 bg-purple-50 text-purple-700'
            : 'border-gray-200 hover:border-purple-300'
        }`}
      >
        <span className={`w-5 h-5 rounded border flex items-center justify-center ${
          selected.includes(opt.value)
            ? 'bg-purple-500 border-purple-500 text-white'
            : 'border-gray-300'
        }`}>
          {selected.includes(opt.value) && <Check className="w-3 h-3" />}
        </span>
        {opt.emoji && <span>{opt.emoji}</span>}
        <span className="flex-1">{opt.label}</span>
      </button>
    ))}
  </div>
);

// Lê o rascunho local salvo pelo auto-save (null se não houver)
const readOrgLocalDraft = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (!u?.email) return null;
    const saved = JSON.parse(localStorage.getItem(`cadastro_org_draft_${u.email}`) || 'null');
    if (saved?.form && (saved.form.name || saved.form.short_description)) return saved;
  } catch { /* ignora */ }
  return null;
};

export default function CadastroOrganizacao() {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [municipios, setMunicipios] = useState([]);
  const [municipio, setMunicipio] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploadingFoto, setUploadingFoto] = useState(false);

  // Moderation
  const { checkMultipleFields, hasBlockingIssues } = useModeration();

  // Form state
  const [restoredDraft] = useState(readOrgLocalDraft);
  const [formData, setFormData] = useState(() => {
    const base = {
    // Step 1: Identification
    name: '',
    entity_type: '',
    responsible_nature: 'pessoa_fisica',
    cnpj: '',
    short_description: '',
    long_description: '',
    areas_of_action: [],
    
    // Step 1: Territory
    municipio_id: '',
    neighborhood: '',
    zone: 'urbana',
    
    // Step 1: Contact
    whatsapp: '',
    email: '',
    website: '',
    instagram: '',
    facebook: '',

    // Step 1: Coletivo informal (exigidos apenas sem CNPJ)
    tempo_atuacao: '',
    foto_atividade_url: '',

    // Step 1: Terms
    role_of_submitter: '',
    relationship_to_entity: '',
    responsibility_term_accepted: false,
    privacy_policy_accepted: false,
    terms_of_use_accepted: false
    };
    return restoredDraft ? { ...base, ...restoredDraft.form } : base;
  });

  // ---- Auto-save local (evita perda de dados em erro/fechamento) ----
  const localDraftKey = user?.email ? `cadastro_org_draft_${user.email}` : null;

  useEffect(() => {
    if (!restoredDraft || !localDraftKey) return;
    const t = setTimeout(() => {
      toast.success('Rascunho recuperado! Continuando de onde você parou.', {
        duration: 8000,
        action: {
          label: 'Começar do zero',
          onClick: () => {
            localStorage.removeItem(localDraftKey);
            window.location.reload();
          },
        },
      });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!localDraftKey) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(localDraftKey, JSON.stringify({ form: formData, savedAt: Date.now() }));
      } catch { /* quota — ignora */ }
    }, 800);
    return () => clearTimeout(t);
  }, [formData, localDraftKey]);

  useEffect(() => {
    loadMunicipios();
  }, []);

  // Load municipality info
  useEffect(() => {
    if (municipioSlug && municipios.length > 0) {
      const found = municipios.find(m => m.slug === municipioSlug);
      if (found) setMunicipio(found);
    }
  }, [municipioSlug, municipios]);

  const loadMunicipios = async () => {
    try {
      const res = await api.get('/municipios');
      setMunicipios(res.data);
      // Also set for select options (keep original format)
    } catch (error) {
      console.error('Erro ao carregar municípios:', error);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const formatPhone = (value) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');
    // Format as (XX) XXXXX-XXXX
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const formatCNPJ = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
  };

  const handleFotoAtividadeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/org-projects/upload-foto-atividade', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateField('foto_atividade_url', res.data.url);
      toast.success('Foto enviada!');
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao enviar a foto');
    } finally {
      setUploadingFoto(false);
      e.target.value = '';
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.length < 3) {
      newErrors.name = 'Nome deve ter pelo menos 3 caracteres';
    }
    if (!formData.entity_type) {
      newErrors.entity_type = 'Selecione o tipo de entidade';
    }
    if (!formData.short_description || formData.short_description.length < 10) {
      newErrors.short_description = 'Descrição deve ter pelo menos 10 caracteres';
    }
    if (formData.areas_of_action.length === 0) {
      newErrors.areas_of_action = 'Selecione pelo menos uma área de atuação';
    }
    if (!formData.municipio_id) {
      newErrors.municipio_id = 'Selecione o município';
    }
    if (!formData.neighborhood) {
      newErrors.neighborhood = 'Informe o bairro';
    }
    if (!formData.whatsapp || formData.whatsapp.replace(/\D/g, '').length < 10) {
      newErrors.whatsapp = 'WhatsApp inválido';
    }
    if (!isInformal && cnpjDigits.length !== 14) {
      newErrors.cnpj = 'CNPJ incompleto. Informe os 14 dígitos ou deixe em branco.';
    }
    if (isInformal) {
      if (!formData.tempo_atuacao) {
        newErrors.tempo_atuacao = 'Informe há quanto tempo o projeto/grupo atua';
      }
      if (!formData.foto_atividade_url) {
        newErrors.foto_atividade_url = 'Envie pelo menos uma foto de atividade real do projeto';
      }
      if (!formData.referencia_apoio || formData.referencia_apoio.trim().length < 3) {
        newErrors.referencia_apoio = 'Informe uma pessoa ou instituição que possa confirmar o projeto/grupo';
      }
    }
    if (!formData.role_of_submitter) {
      newErrors.role_of_submitter = 'Selecione seu papel';
    }
    if (!formData.relationship_to_entity) {
      newErrors.relationship_to_entity = 'Selecione sua relação';
    }
    if (!formData.responsibility_term_accepted) {
      newErrors.responsibility_term_accepted = 'Você deve aceitar o termo de responsabilidade';
    }
    if (!formData.privacy_policy_accepted) {
      newErrors.privacy_policy_accepted = 'Você deve aceitar a política de privacidade';
    }
    if (!formData.terms_of_use_accepted) {
      newErrors.terms_of_use_accepted = 'Você deve aceitar os termos de uso';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep1()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Você precisa estar logado para cadastrar uma organização');
      navigate('/perfil');
      return;
    }

    setLoading(true);
    try {
      // Check moderation for text fields
      const fieldsToModerate = [
        { field: 'name', text: formData.name, context: MODERATION_CONTEXTS.ORGANIZATION_NAME },
        { field: 'short_description', text: formData.short_description, context: MODERATION_CONTEXTS.ORGANIZATION_DESCRIPTION },
      ];
      
      const results = await checkMultipleFields(fieldsToModerate);
      
      // Check if any content was blocked
      if (hasBlockingIssues(results)) {
        const blockedFields = Object.entries(results)
          .filter(([_, r]) => r.action === MODERATION_ACTIONS.BLOCKED)
          .map(([field, _]) => {
            const labels = { name: 'Nome', short_description: 'Descrição' };
            return labels[field] || field;
          });
        
        toast.error(`Os seguintes campos contêm conteúdo inadequado: ${blockedFields.join(', ')}`);
        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        whatsapp: formData.whatsapp.replace(/\D/g, ''),
        cnpj: formData.cnpj ? formData.cnpj.replace(/\D/g, '') : null
      };

      const res = await api.post('/org-projects', payload);

      if (localDraftKey) localStorage.removeItem(localDraftKey);
      toast.success('Organização enviada para análise da gestão municipal! Enquanto isso, adicione o logo e fotos da galeria.');
      // Redirect to edit page so user can immediately upload media via ImageUploader
      navigate(`/social/${res.data.id}/editar?tab=media`);
    } catch (error) {
      console.error('Erro ao cadastrar:', error);
      const d = error.response?.data?.detail;
      const message = (typeof d === 'object' && d?.message) ? d.message : (d || 'Erro ao cadastrar organização');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Iter165 — classificação automática (definida pelo sistema)
  const cnpjDigits = (formData.cnpj || '').replace(/\D/g, '');
  const isInformal = cnpjDigits.length === 0;

  // Check authentication
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md text-center shadow-lg">
          <Heart className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Faça login para continuar
          </h2>
          <p className="text-gray-600 mb-6">
            Para cadastrar uma organização, você precisa ter uma conta na plataforma.
          </p>
          <button
            onClick={() => navigate('/perfil')}
            className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700"
          >
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Breadcrumbs 
            items={[
              { label: 'Impacto Social', path: '/social' },
              { label: 'Cadastrar Organização', path: null }
            ]}
            municipio={municipio}
            className="mb-4"
          />
          <div className="flex items-center gap-3">
            <Heart className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Cadastrar Organização</h1>
              <p className="text-purple-100 text-sm">
                Cadastre seu projeto social, ONG ou associação
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Etapa {currentStep} de 1
            </span>
            <span className="text-sm text-gray-500">
              Identificação + Território + Contato
            </span>
          </div>
          <ProgressBar currentStep={currentStep} totalSteps={1} />
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {/* Info Alert */}
          <div className="flex gap-3 p-4 bg-purple-50 rounded-xl mb-6">
            <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-800">
              <p className="font-medium">Cadastro rápido em 1 etapa</p>
              <p className="text-purple-600">
                Complete as informações básicas agora. Após o envio, o cadastro passa por
                <strong> análise da gestão municipal</strong> antes de ser publicado. Você poderá
                adicionar mais detalhes depois, como formas de apoio, atividades e métricas de impacto.
              </p>
            </div>
          </div>

          {/* Section: Identification */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Identificação
            </h3>

            <FormField label="Nome da organização/projeto" required error={errors.name}>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Ex: ONG Amigos do Bairro"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="org-name-input"
              />
            </FormField>

            <FormField label="Tipo de entidade" required error={errors.entity_type}>
              <SelectField
                value={formData.entity_type}
                onChange={(v) => updateField('entity_type', v)}
                options={ENTITY_TYPES}
                placeholder="Selecione o tipo..."
                data-testid="org-type-select"
              />
            </FormField>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-5" data-testid="coletivo-redirect-alert">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-purple-900">
                    É um coletivo de empreendedores? (feira, artesãos, cooperativa, produtores)
                  </p>
                  <p className="text-purple-800 mt-1">
                    Coletivos têm cadastro próprio, com gestão de membros e agenda de eventos.
                    Este formulário é exclusivo para <strong>organizações e projetos sociais</strong>.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/coletivos/cadastrar')}
                    className="mt-2 text-purple-700 font-semibold underline hover:text-purple-900"
                    data-testid="coletivo-redirect-button"
                  >
                    Ir para o cadastro de Coletivo →
                  </button>
                </div>
              </div>
            </div>

            <FormField label="Natureza do responsável" required>
              <SelectField
                value={formData.responsible_nature}
                onChange={(v) => updateField('responsible_nature', v)}
                options={RESPONSIBLE_NATURE}
              />
            </FormField>

            {formData.responsible_nature === 'pessoa_juridica' && (
              <FormField label="CNPJ" error={errors.cnpj} hint="Com CNPJ, o cadastro é classificado automaticamente como Organização Formal. Sem CNPJ, como Projeto Social Informal (com validação adicional).">
                <input
                  type="text"
                  value={formData.cnpj}
                  onChange={(e) => updateField('cnpj', formatCNPJ(e.target.value))}
                  placeholder="00.000.000/0000-00"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  data-testid="org-cnpj-input"
                />
              </FormField>
            )}

            <div className={`flex items-center gap-2 p-3 rounded-xl text-sm mb-4 ${
              isInformal ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-blue-50 text-blue-800 border border-blue-200'
            }`} data-testid="org-classificacao-info">
              <Info className="w-4 h-4 flex-shrink-0" />
              <span>
                Classificação automática: <strong>{isInformal ? 'Projeto Social Informal' : 'Organização Formal'}</strong>
                {isInformal && ' — serão solicitadas informações adicionais abaixo.'}
              </span>
            </div>

            <FormField 
              label="Descrição curta" 
              required 
              error={errors.short_description}
              hint={`${formData.short_description.length}/250 caracteres`}
            >
              <textarea
                value={formData.short_description}
                onChange={(e) => updateField('short_description', e.target.value.slice(0, 250))}
                placeholder="Descreva brevemente a missão e principais atividades..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
                data-testid="org-description-input"
              />
            </FormField>

            <FormField label="Descrição completa" hint="Opcional — conte a história, missão, atividades e conquistas da organização. Você também pode preencher depois, na edição.">
              <textarea
                value={formData.long_description}
                onChange={(e) => updateField('long_description', e.target.value)}
                placeholder="Detalhe a atuação da organização/projeto..."
                rows={5}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
                data-testid="org-long-description-input"
              />
            </FormField>

            <FormField label="Áreas de atuação" required error={errors.areas_of_action}>
              <CheckboxGroup
                options={AREAS_OF_ACTION}
                selected={formData.areas_of_action}
                onChange={(v) => updateField('areas_of_action', v)}
              />
            </FormField>
          </div>

          {/* Section: Territory */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              Território
            </h3>

            <FormField label="Município" required error={errors.municipio_id}>
              <SelectField
                value={formData.municipio_id}
                onChange={(v) => updateField('municipio_id', v)}
                options={municipios.map(m => ({ value: m.id, label: m.nome }))}
                placeholder="Selecione o município..."
                data-testid="org-municipio-select"
              />
            </FormField>

            <FormField label="Bairro principal" required error={errors.neighborhood}>
              <input
                type="text"
                value={formData.neighborhood}
                onChange={(e) => updateField('neighborhood', e.target.value)}
                placeholder="Ex: Centro, Bela Vista..."
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="org-neighborhood-input"
              />
            </FormField>

            <FormField label="Zona">
              <SelectField
                value={formData.zone}
                onChange={(v) => updateField('zone', v)}
                options={ZONES}
              />
            </FormField>
          </div>

          {/* Section: Contact */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-purple-600" />
              Contato
            </h3>

            <FormField label="WhatsApp" required error={errors.whatsapp}>
              <input
                type="tel"
                value={formData.whatsapp}
                onChange={(e) => updateField('whatsapp', formatPhone(e.target.value))}
                placeholder="(31) 99999-9999"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="org-whatsapp-input"
              />
            </FormField>

            <FormField label="E-mail" hint="Opcional">
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="contato@organizacao.org"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="org-email-input"
              />
            </FormField>

            <FormField label="Site" hint="Opcional">
              <input
                type="text"
                value={formData.website}
                onChange={(e) => updateField('website', e.target.value)}
                placeholder="https://www.organizacao.org"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="org-website-input"
              />
            </FormField>

            <FormField label="Instagram" hint="Opcional — link ou @usuario">
              <input
                type="text"
                value={formData.instagram}
                onChange={(e) => updateField('instagram', e.target.value)}
                placeholder="@suaorganizacao ou instagram.com/suaorganizacao"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="org-instagram-input"
              />
            </FormField>

            <FormField label="Facebook" hint="Opcional — link da página">
              <input
                type="text"
                value={formData.facebook}
                onChange={(e) => updateField('facebook', e.target.value)}
                placeholder="facebook.com/suaorganizacao"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="org-facebook-input"
              />
            </FormField>
          </div>

          {/* Section: Coletivo informal (apenas sem CNPJ) */}
          {isInformal && (
            <div className="mb-8" data-testid="informal-section">
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Sobre o projeto/grupo
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Como o cadastro não possui CNPJ, precisamos de algumas informações
                que ajudam a gestão municipal a confirmar que o projeto existe e atua de verdade.
              </p>

              <FormField label="Há quanto tempo o projeto/grupo atua?" required error={errors.tempo_atuacao} hint="Mesmo que informalmente">
                <SelectField
                  value={formData.tempo_atuacao}
                  onChange={(v) => updateField('tempo_atuacao', v)}
                  options={TEMPO_ATUACAO_OPTIONS}
                  placeholder="Selecione o período..."
                  data-testid="org-tempo-atuacao-select"
                />
              </FormField>

              <FormField
                label="Foto de uma atividade real"
                required
                error={errors.foto_atividade_url}
                hint="Uma imagem de ação, evento ou atividade do projeto — não use logotipo, banner ou imagem genérica."
              >
                {formData.foto_atividade_url ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={formData.foto_atividade_url}
                      alt="Atividade do projeto"
                      className="w-24 h-24 rounded-xl object-cover border border-gray-200"
                      data-testid="org-foto-atividade-preview"
                    />
                    <label className="px-4 py-2 rounded-xl border border-purple-200 text-purple-600 text-sm font-medium cursor-pointer hover:bg-purple-50">
                      Trocar foto
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleFotoAtividadeUpload} />
                    </label>
                  </div>
                ) : (
                  <label
                    className={`flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                      errors.foto_atividade_url ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/40'
                    }`}
                    data-testid="org-foto-atividade-dropzone"
                  >
                    {uploadingFoto ? (
                      <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                    ) : (
                      <Camera className="w-6 h-6 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-600 font-medium">
                      {uploadingFoto ? 'Enviando...' : 'Clique para enviar uma foto (JPG/PNG, até 5MB)'}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={handleFotoAtividadeUpload}
                      disabled={uploadingFoto}
                      data-testid="org-foto-atividade-input"
                    />
                  </label>
                )}
              </FormField>
            </div>
          )}

          {/* Section: Terms */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              Declarações e Termos
            </h3>

            <FormField label="Seu papel na organização" required error={errors.role_of_submitter}>
              <SelectField
                value={formData.role_of_submitter}
                onChange={(v) => updateField('role_of_submitter', v)}
                options={SUBMITTER_ROLES}
                placeholder="Selecione seu papel..."
              />
            </FormField>

            <FormField label="Sua relação com a entidade" required error={errors.relationship_to_entity}>
              <SelectField
                value={formData.relationship_to_entity}
                onChange={(v) => updateField('relationship_to_entity', v)}
                options={RELATIONSHIPS}
                placeholder="Selecione sua relação..."
              />
            </FormField>

            <div className="space-y-3 mt-4">
              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                formData.responsibility_term_accepted
                  ? 'border-purple-500 bg-purple-50'
                  : errors.responsibility_term_accepted
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-200 hover:border-purple-300'
              }`}>
                <input
                  type="checkbox"
                  checked={formData.responsibility_term_accepted}
                  onChange={(e) => updateField('responsibility_term_accepted', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  data-testid="org-terms-checkbox"
                />
                <div className="text-sm">
                  <p className="font-medium text-gray-900">
                    Termo de Responsabilidade <span className="text-red-500">*</span>
                  </p>
                  <p className="text-gray-600">
                    Declaro que as informações prestadas são verdadeiras e me responsabilizo 
                    por este cadastro em nome da organização/projeto.
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                formData.privacy_policy_accepted
                  ? 'border-purple-500 bg-purple-50'
                  : errors.privacy_policy_accepted
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-200 hover:border-purple-300'
              }`}>
                <input
                  type="checkbox"
                  checked={formData.privacy_policy_accepted}
                  onChange={(e) => updateField('privacy_policy_accepted', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="text-sm">
                  <p className="font-medium text-gray-900">
                    Política de Privacidade <span className="text-red-500">*</span>
                  </p>
                  <p className="text-gray-600">
                    Li e aceito a{' '}
                    <a href="/politica-de-privacidade" target="_blank" className="text-purple-600 underline">
                      Política de Privacidade
                    </a>
                    {' '}da plataforma.
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                formData.terms_of_use_accepted
                  ? 'border-purple-500 bg-purple-50'
                  : errors.terms_of_use_accepted
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-200 hover:border-purple-300'
              }`}>
                <input
                  type="checkbox"
                  checked={formData.terms_of_use_accepted}
                  onChange={(e) => updateField('terms_of_use_accepted', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="text-sm">
                  <p className="font-medium text-gray-900">
                    Termos de Uso <span className="text-red-500">*</span>
                  </p>
                  <p className="text-gray-600">
                    Li e aceito os{' '}
                    <a href="/termos-de-uso" target="_blank" className="text-purple-600 underline">
                      Termos de Uso
                    </a>
                    {' '}da plataforma.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-bold text-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="org-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Cadastrando...
              </>
            ) : (
              <>
                Cadastrar Organização
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          <p className="text-center text-sm text-gray-500 mt-4">
            Após o cadastro, você poderá adicionar formas de apoio, 
            atividades e métricas de impacto.
          </p>
        </div>
      </div>
    </div>
  );
}
